/**
 * Centralized file type rules used across chat, agents, and project knowledge base.
 * When adding support for a new file type, update ONLY this file.
 */

/** Document extensions that require server-side parsing (parse-document edge function) */
export const DOC_EXTENSIONS = ['pdf', 'docx', 'pptx', 'xlsx', 'txt', 'md', 'csv', 'html', 'htm'] as const;

/** Image extensions sent as base64 for vision models */
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'] as const;

/** Audio extensions sent as base64 to models with `audio` capability */
export const AUDIO_EXTENSIONS = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'webm', 'flac'] as const;

/** Font extensions stored as binary assets (not parsed for text) */
export const FONT_EXTENSIONS = ['ttf', 'otf', 'woff', 'woff2', 'eot'] as const;

/** All supported extensions (documents + images + audio + fonts) */
export const ALL_EXTENSIONS = [...DOC_EXTENSIONS, ...IMAGE_EXTENSIONS, ...AUDIO_EXTENSIONS, ...FONT_EXTENSIONS] as const;

/** HTML accept attribute for file inputs — documents only */
export const ACCEPT_DOCS = DOC_EXTENSIONS.map(e => `.${e}`).join(',');

/**
 * HTML accept attribute para uploads de bases de conhecimento (projeto/agente).
 * Áudio fica de fora aqui — parse-document não processa áudio; áudio só faz
 * sentido como anexo de chat em modelos com capacidade `audio`.
 */
export const ACCEPT_ALL = [...DOC_EXTENSIONS, ...IMAGE_EXTENSIONS, ...FONT_EXTENSIONS]
  .map(e => `.${e}`).join(',');

/** HTML accept attribute para anexos de áudio em chat (modelos com capacidade `audio`) */
export const ACCEPT_AUDIO = AUDIO_EXTENSIONS.map(e => `.${e}`).join(',');

/** Maximum file size in bytes (128 MB) — limite geral de upload */
export const MAX_FILE_SIZE = 128 * 1024 * 1024;

/**
 * Tamanho maximo para arquivos que vao pelo parse-document.
 * Edge function rejeita acima disso pra evitar timeout silencioso da
 * Vercel e OOM no extrator. Imagens nao parsam, entao nao se aplica.
 * Manter sincronizado com MAX_FILE_SIZE_BYTES em supabase/functions/parse-document/index.ts.
 */
export const MAX_PARSEABLE_FILE_SIZE = 25 * 1024 * 1024;

/**
 * Tamanho máximo para anexos de áudio (200 MB). Modelos com capacidade
 * `audio` (Gemini 2.5/3.x) aceitam até ~8h por requisição. O upload vai
 * direto pro Storage (bucket chat-files, limite 200 MB) e a edge function
 * `chat` baixa server-side e injeta no payload — não passa pelo body da
 * edge, então o teto real é o limite do bucket e o tempo de upload.
 */
export const MAX_AUDIO_FILE_SIZE = 200 * 1024 * 1024;

/** Maximum attachments per chat message */
export const MAX_CHAT_ATTACHMENTS = 5;

/** Maximum files per agent */
export const MAX_AGENT_FILES = 10;

/** Maximum files per project knowledge base */
export const MAX_PROJECT_FILES = 10;

/** Maximum token budget for project knowledge base */
export const MAX_PROJECT_TOKENS = 200_000;

/** Maps file extension to a normalized type key used in the database */
export const FILE_TYPE_MAP: Record<string, string> = {
  pdf: 'pdf', docx: 'docx', doc: 'docx', pptx: 'pptx', ppt: 'pptx',
  xlsx: 'xlsx', xls: 'xlsx', txt: 'txt', md: 'md', csv: 'csv',
  html: 'html', htm: 'html',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image',
  mp3: 'audio', wav: 'audio', m4a: 'audio', aac: 'audio', ogg: 'audio', webm: 'audio', flac: 'audio',
  ttf: 'font', otf: 'font', woff: 'font', woff2: 'font', eot: 'font',
};

/** Returns the normalized file type for a given file name */
export function getFileType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return FILE_TYPE_MAP[ext] || ext;
}

/** Checks if a file extension is an image */
export function isImageExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return (IMAGE_EXTENSIONS as readonly string[]).includes(ext);
}

/** Checks if a file extension is a supported document */
export function isDocExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return (DOC_EXTENSIONS as readonly string[]).includes(ext);
}

/** Checks if a file extension is a font asset */
export function isFontExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return (FONT_EXTENSIONS as readonly string[]).includes(ext);
}

/** Checks if a file extension is an audio file */
export function isAudioExtension(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return (AUDIO_EXTENSIONS as readonly string[]).includes(ext);
}

/** Checks if a file extension is supported at all */
export function isSupportedExtension(fileName: string): boolean {
  return isImageExtension(fileName) || isDocExtension(fileName) || isFontExtension(fileName) || isAudioExtension(fileName);
}

/** Formats byte count to human-readable string */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Valida se o arquivo passa pelos limites antes de subir.
 * Retorna mensagem de erro pronta pra toast, ou null se OK.
 * Áudio tem teto próprio (200 MB) — não é limitado pelo MAX_FILE_SIZE geral.
 */
export function validateUploadSize(file: File): string | null {
  const isAudio = isAudioExtension(file.name);

  if (isAudio) {
    if (file.size > MAX_AUDIO_FILE_SIZE) {
      const limitMB = Math.round(MAX_AUDIO_FILE_SIZE / (1024 * 1024));
      return (
        `Áudio muito grande (${formatFileSize(file.size)}). Limite: ${limitMB} MB. ` +
        `Divida em trechos menores antes de subir.`
      );
    }
    return null;
  }

  if (file.size > MAX_FILE_SIZE) {
    return `Arquivo excede 128 MB (atual: ${formatFileSize(file.size)})`;
  }
  if (isDocExtension(file.name) && file.size > MAX_PARSEABLE_FILE_SIZE) {
    const limitMB = Math.round(MAX_PARSEABLE_FILE_SIZE / (1024 * 1024));
    return (
      `Arquivo muito grande pra parsear (${formatFileSize(file.size)}). ` +
      `Limite atual: ${limitMB} MB. Divida o arquivo em partes menores ` +
      `ou converta pra texto antes de subir.`
    );
  }
  return null;
}
